// Pure, client-safe model for the all-products category tiles and chips.
// Must not import anything server-only.
import type { FacetCategoryDTO } from "./listService";

export type ListingDisplay = "tiles" | "chips" | "hidden";
export const TREE_GROUP = "tree";
export const CHIP_TOGGLE_MAX = 4;

const MAX_DEPTH = 10;

export type CategoryTreeRow = {
  id: string;
  slug: string;
  parent_id: string | null;
  listing_display: ListingDisplay | null;
};

/** Root ancestor of `id` (itself if top level). Depth guard 10, like findCategoriesForProductIds. */
export function rootIdOf(
  byId: Map<string, CategoryTreeRow>,
  id: string,
): string {
  let current = id;
  for (let i = 0; i < MAX_DEPTH; i++) {
    const parentId = byId.get(current)?.parent_id;
    if (!parentId || !byId.has(parentId)) break;
    current = parentId;
  }
  return current;
}

/** Group key of a category: the root's id when that root is `chips`, else TREE_GROUP. */
export function groupKeyOf(
  byId: Map<string, CategoryTreeRow>,
  id: string,
): string {
  const rootId = rootIdOf(byId, id);
  return byId.get(rootId)?.listing_display === "chips" ? rootId : TREE_GROUP;
}

/** URL slugs → group key → slugs. Unknown slugs go to TREE_GROUP. */
export function groupCategorySlugs(
  rows: CategoryTreeRow[],
  slugs: string[],
): Map<string, string[]> {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const bySlug = new Map(rows.map((r) => [r.slug, r]));
  const groups = new Map<string, string[]>();
  for (const slug of slugs) {
    const row = bySlug.get(slug);
    const key = row ? groupKeyOf(byId, row.id) : TREE_GROUP;
    const list = groups.get(key);
    if (list) list.push(slug);
    else groups.set(key, [slug]);
  }
  return groups;
}

export type CategoryNavTile = {
  slug: string;
  name: string;
  image_url: string | null;
  blur_data_url: string | null;
  selected: boolean;
};
export type CategoryChipGroup = {
  rootSlug: string;
  name: string;
  mode: "toggles" | "sheet";
  options: { slug: string; name: string; selected: boolean }[];
};
export type CategoryNav = {
  tiles: CategoryNavTile[];
  /** "All of <parent>" tile shown first when drilled in; null at level 0. */
  allTile: { slug: string; name: string; selected: boolean } | null;
  /** Ancestors of the visible level, below any `tiles` root, root-first. Empty at level 0. */
  trail: { slug: string; name: string }[];
  chipGroups: CategoryChipGroup[];
  /** Applied slugs in the tile tree (TREE_GROUP), as the server grouped them. */
  treeSlugs: string[];
};

/**
 * `treeSlugs` comes from the server (ProductListingDTO.appliedTreeSlugs), which
 * groups against the full category tree. It cannot be inferred from the facet:
 * a selected chip with no products under the current tile is absent from it.
 */
export function buildCategoryNav(
  categories: FacetCategoryDTO[],
  appliedSlugs: string[],
  treeSlugs: string[],
): CategoryNav {
  const bySlug = new Map(categories.map((c) => [c.slug, c]));
  const parentOf = (c: FacetCategoryDTO) =>
    (c.parentSlug && bySlug.get(c.parentSlug)) || null;
  const rootOf = (c: FacetCategoryDTO) => {
    let cur = c;
    for (let i = 0; i < MAX_DEPTH; i++) {
      const p = parentOf(cur);
      if (!p) break;
      cur = p;
    }
    return cur;
  };
  const childrenOf = (slug: string) =>
    categories.filter((c) => c.parentSlug === slug);

  const toTile = (c: FacetCategoryDTO): CategoryNavTile => ({
    slug: c.slug,
    name: c.name,
    image_url: c.image_url,
    blur_data_url: c.blur_data_url,
    selected: treeSlugs.includes(c.slug),
  });

  const level0 = categories.filter((c) => {
    const root = rootOf(c);
    if (root.listingDisplay === "chips" || root.listingDisplay === "hidden") {
      return false;
    }
    // A category whose parent is missing from the facet is an orphan: not a tile.
    if (root.parentSlug) return false;
    return root === c
      ? root.listingDisplay === null
      : parentOf(c) === root && root.listingDisplay === "tiles";
  });

  const isTilesRoot = (c: FacetCategoryDTO) =>
    c.listingDisplay === "tiles" && !parentOf(c);

  /** Ancestors of `c` (inclusive) below any tiles root, root-first. */
  const chainTo = (c: FacetCategoryDTO) => {
    const chain: FacetCategoryDTO[] = [];
    let cur: FacetCategoryDTO | null = c;
    for (let i = 0; i < MAX_DEPTH && cur; i++) {
      chain.unshift(cur);
      cur = parentOf(cur);
    }
    return chain.filter((x) => !isTilesRoot(x));
  };

  let tiles = level0.map(toTile);
  let allTile: CategoryNav["allTile"] = null;
  let trail: CategoryNav["trail"] = [];

  // Drill from the first tree slug the facet knows; none → stay at level 0.
  const s = treeSlugs
    .map((slug) => bySlug.get(slug))
    .find((c) => c !== undefined);
  if (s && rootOf(s).listingDisplay !== "hidden") {
    const kids = childrenOf(s.slug);
    if (kids.length) {
      tiles = kids.map(toTile);
      allTile = { slug: s.slug, name: s.name, selected: true };
      trail = chainTo(s).map(({ slug, name }) => ({ slug, name }));
    } else {
      const parent = parentOf(s);
      if (parent && !isTilesRoot(parent)) {
        tiles = childrenOf(parent.slug).map(toTile);
        allTile = { slug: parent.slug, name: parent.name, selected: false };
        trail = chainTo(parent).map(({ slug, name }) => ({ slug, name }));
      }
    }
  }

  const chipGroups: CategoryChipGroup[] = categories
    .filter((c) => c.listingDisplay === "chips" && !parentOf(c))
    .map((root) => {
      const options = childrenOf(root.slug).map((c) => ({
        slug: c.slug,
        name: c.name,
        selected: appliedSlugs.includes(c.slug),
      }));
      return {
        rootSlug: root.slug,
        name: root.name,
        mode: options.length <= CHIP_TOGGLE_MAX ? "toggles" : "sheet",
        options,
      };
    });

  return { tiles, allTile, trail, chipGroups, treeSlugs };
}

/** Replace exactly the tree slugs in `applied` with `slug` (or remove them when null); every other slug untouched. */
export function withTreeSelection(
  applied: string[],
  nav: CategoryNav,
  slug: string | null,
): string[] {
  const rest = applied.filter((s) => !nav.treeSlugs.includes(s));
  return slug === null ? rest : [...rest, slug];
}

/** Add or remove one chip slug. */
export function toggleSlug(applied: string[], slug: string): string[] {
  return applied.includes(slug)
    ? applied.filter((s) => s !== slug)
    : [...applied, slug];
}
